<template>
  <section class="system-performance-section">
    <div class="section-header">
      <h2 class="section-title">System Performance</h2>
      <div class="time-filter">
        <select v-model="timeframe" class="filter-select">
          <option value="1h">Last Hour</option>
          <option value="24h">Last 24 Hours</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
        </select>
      </div>
    </div>
    
    <div class="performance-grid">
      <!-- Server Uptime -->
      <div class="performance-card">
        <div class="card-header">
          <h3 class="card-title">Server Uptime</h3>
          <div class="status-indicator" :class="getStatusClass(performanceData.uptime.status)"></div>
        </div>
        <div class="card-content">
          <div class="uptime-display">
            <div class="main-value">{{ formatUptime(performanceData.uptime.value) }}</div>
            <div class="sub-value">{{ formatUptimePercentage(performanceData.uptime.percentage) }}</div>
          </div>
          <div class="mini-chart uptime-chart">
            <div v-for="(val, idx) in performanceData.uptime.history" 
                 :key="'uptime-'+idx" 
                 class="chart-bar" 
                 :class="{ 'downtime': val < 90 }"
                 :style="{ height: `${val}%` }">
            </div>
          </div>
        </div>
      </div>
      
      <!-- API Response Time -->
      <div class="performance-card">
        <div class="card-header">
          <h3 class="card-title">API Response Time</h3>
          <div class="status-indicator" :class="getStatusClass(performanceData.api.status)"></div>
        </div>
        <div class="card-content">
          <div class="metric-display">
            <div class="main-value">{{ performanceData.api.average_response }}ms</div>
            <div class="sub-value">
              <span :class="getChangeClass(performanceData.api.change)">
                {{ performanceData.api.change > 0 ? '+' : '' }}{{ performanceData.api.change }}ms
              </span>
              from previous {{ timeframe }}
            </div>
          </div>
          <div class="mini-chart api-chart">
            <div v-for="(val, idx) in performanceData.api.history" 
                 :key="'api-'+idx" 
                 class="chart-bar"
                 :class="{ 'warning': val > performanceData.api.threshold.warning, 'critical': val > performanceData.api.threshold.critical }"
                 :style="{ height: `${(val / performanceData.api.max_value) * 100}%` }">
            </div>
          </div>
        </div>
      </div>
      
      <!-- Error Rate -->
      <div class="performance-card">
        <div class="card-header">
          <h3 class="card-title">Error Rate</h3>
          <div class="status-indicator" :class="getStatusClass(performanceData.errors.status)"></div>
        </div>
        <div class="card-content">
          <div class="metric-display">
            <div class="main-value">{{ performanceData.errors.rate }}%</div>
            <div class="sub-value">
              <span :class="getChangeClass(-performanceData.errors.change)">
                {{ performanceData.errors.change > 0 ? '+' : '' }}{{ performanceData.errors.change }}%
              </span>
              from previous {{ timeframe }}
            </div>
          </div>
          <div class="mini-chart error-chart">
            <div v-for="(val, idx) in performanceData.errors.history" 
                 :key="'error-'+idx" 
                 class="chart-bar"
                 :class="{ 'warning': val > performanceData.errors.threshold.warning, 'critical': val > performanceData.errors.threshold.critical }"
                 :style="{ height: `${(val / performanceData.errors.max_value) * 100}%` }">
            </div>
          </div>
        </div>
      </div>
      
      <!-- Database Performance -->
      <div class="performance-card">
        <div class="card-header">
          <h3 class="card-title">Database Performance</h3>
          <div class="status-indicator" :class="getStatusClass(performanceData.database.status)"></div>
        </div>
        <div class="card-content">
          <div class="metric-display">
            <div class="main-value">{{ performanceData.database.query_time }}ms</div>
            <div class="sub-value">
              <span :class="getChangeClass(performanceData.database.change)">
                {{ performanceData.database.change > 0 ? '+' : '' }}{{ performanceData.database.change }}ms
              </span>
              from previous {{ timeframe }}
            </div>
          </div>
          <div class="mini-chart db-chart">
            <div v-for="(val, idx) in performanceData.database.history" 
                 :key="'db-'+idx" 
                 class="chart-bar"
                 :class="{ 'warning': val > performanceData.database.threshold.warning, 'critical': val > performanceData.database.threshold.critical }"
                 :style="{ height: `${(val / performanceData.database.max_value) * 100}%` }">
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="alerts-container" v-if="performanceData.alerts && performanceData.alerts.length > 0">
      <h3 class="alerts-title">Recent Alerts</h3>
      <div class="alerts-list">
        <div v-for="(alert, index) in performanceData.alerts" :key="index" class="alert-item" :class="'alert-' + alert.severity">
          <div class="alert-icon">
            <svg v-if="alert.severity === 'critical'" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            <svg v-else-if="alert.severity === 'warning'" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="alert-content">
            <div class="alert-message">{{ alert.message }}</div>
            <div class="alert-timestamp">{{ formatAlertTime(alert.timestamp) }}</div>
          </div>
          <div class="alert-actions">
            <button class="action-button">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, computed } from 'vue';

export default {
  name: 'SystemPerformanceSection',
  props: {
    performanceMetrics: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const timeframe = ref('24h');
    
    // Use API data or fallback to dummy data
    const performanceData = computed(() => {
      if (props.performanceMetrics && Object.keys(props.performanceMetrics).length > 0) {
        return props.performanceMetrics;
      }
      
      // Dummy data
      return {
        uptime: {
          value: 29.7 * 24, // days in hours
          percentage: 99.98,
          status: 'healthy',
          history: [100, 100, 100, 100, 100, 80, 100, 100, 100, 100, 100, 100]
        },
        api: {
          average_response: 248,
          change: -12,
          status: 'healthy',
          history: [280, 260, 290, 270, 250, 260, 240, 250, 230, 240, 220, 248],
          threshold: {
            warning: 300,
            critical: 500
          },
          max_value: 500
        },
        errors: {
          rate: 0.42,
          change: -0.18,
          status: 'healthy',
          history: [0.8, 0.7, 0.9, 0.6, 0.5, 0.6, 0.4, 0.5, 0.3, 0.4, 0.5, 0.42],
          threshold: {
            warning: 1.0,
            critical: 2.0
          },
          max_value: 3.0
        },
        database: {
          query_time: 87,
          change: 5,
          status: 'healthy',
          history: [75, 80, 78, 82, 85, 79, 80, 83, 85, 82, 84, 87],
          threshold: {
            warning: 120,
            critical: 200
          },
          max_value: 250
        },
        alerts: [
          {
            severity: 'warning',
            message: 'Increased error rate detected in authentication service',
            timestamp: new Date(Date.now() - 95 * 60 * 1000) // 95 minutes ago
          },
          {
            severity: 'info',
            message: 'Routine database maintenance scheduled for Sunday 02:00 UTC',
            timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000) // 4 hours ago
          },
          {
            severity: 'critical',
            message: 'API server experienced 4 minutes of downtime',
            timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000) // 2 days ago
          }
        ]
      };
    });
    
    // Helper for status indicator classes
    const getStatusClass = (status) => {
      switch(status) {
        case 'critical':
          return 'status-critical';
        case 'warning':
          return 'status-warning';
        case 'healthy':
        default:
          return 'status-healthy';
      }
    };
    
    // Helper for change indicator classes
    const getChangeClass = (change) => {
      if (change > 0) {
        return 'change-negative';
      } else if (change < 0) {
        return 'change-positive';
      }
      return 'change-neutral';
    };
    
    // Format uptime from hours to days, hours
    const formatUptime = (hours) => {
      const days = Math.floor(hours / 24);
      const remainingHours = Math.floor(hours % 24);
      return `${days}d ${remainingHours}h`;
    };
    
    // Format uptime percentage
    const formatUptimePercentage = (percentage) => {
      return `${percentage.toFixed(2)}% uptime`;
    };
    
    // Format alert timestamp
    const formatAlertTime = (timestamp) => {
      const now = new Date();
      const diff = now - timestamp;
      
      // Less than an hour
      if (diff < 60 * 60 * 1000) {
        const minutes = Math.floor(diff / (60 * 1000));
        return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
      }
      
      // Less than a day
      if (diff < 24 * 60 * 60 * 1000) {
        const hours = Math.floor(diff / (60 * 60 * 1000));
        return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
      }
      
      // Less than a week
      if (diff < 7 * 24 * 60 * 60 * 1000) {
        const days = Math.floor(diff / (24 * 60 * 60 * 1000));
        return `${days} day${days !== 1 ? 's' : ''} ago`;
      }
      
      // Format as date
      return timestamp.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    
    return {
      timeframe,
      performanceData,
      getStatusClass,
      getChangeClass,
      formatUptime,
      formatUptimePercentage,
      formatAlertTime
    };
  }
};
</script>

<style scoped>
.system-performance-section {
  @apply bg-white rounded-lg shadow p-6;
}

.section-header {
  @apply flex justify-between items-center mb-6;
}

.section-title {
  @apply text-xl font-semibold text-gray-800;
}

.time-filter {
  @apply relative;
}

.filter-select {
  @apply bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg 
  focus:ring-blue-500 focus:border-blue-500 block p-2.5;
}

.performance-grid {
  @apply grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6;
}

.performance-card {
  @apply bg-gray-50 rounded-lg p-4;
}

.card-header {
  @apply flex justify-between items-center mb-3;
}

.card-title {
  @apply text-sm font-medium text-gray-700;
}

.status-indicator {
  @apply inline-block h-3 w-3 rounded-full;
}

.status-healthy {
  @apply bg-green-500;
}

.status-warning {
  @apply bg-yellow-500;
}

.status-critical {
  @apply bg-red-500;
}

.card-content {
  @apply flex flex-col;
}

.uptime-display, .metric-display {
  @apply mb-3;
}

.main-value {
  @apply text-2xl font-bold text-gray-800;
}

.sub-value {
  @apply text-sm text-gray-600;
}

.change-positive {
  @apply text-green-600;
}

.change-negative {
  @apply text-red-600;
}

.change-neutral {
  @apply text-gray-600;
}

.mini-chart {
  @apply flex items-end h-20 space-x-1 mt-2;
}

.chart-bar {
  @apply flex-1 bg-blue-400 rounded-t;
}

.chart-bar.warning {
  @apply bg-yellow-400;
}

.chart-bar.critical {
  @apply bg-red-400;
}

.chart-bar.downtime {
  @apply bg-red-400;
}

.alerts-container {
  @apply mt-6 bg-gray-50 rounded-lg p-4;
}

.alerts-title {
  @apply text-lg font-semibold text-gray-800 mb-4;
}

.alerts-list {
  @apply space-y-3;
}

.alert-item {
  @apply flex items-start p-3 rounded-lg;
}

.alert-critical {
  @apply bg-red-50 text-red-800;
}

.alert-warning {
  @apply bg-yellow-50 text-yellow-800;
}

.alert-info {
  @apply bg-blue-50 text-blue-800;
}

.alert-icon {
  @apply flex-shrink-0 mt-0.5 mr-3;
}

.alert-content {
  @apply flex-1;
}

.alert-message {
  @apply text-sm font-medium;
}

.alert-timestamp {
  @apply text-xs mt-1 opacity-80;
}

.alert-actions {
  @apply flex-shrink-0 ml-3;
}

.action-button {
  @apply p-1 rounded-full hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500;
}

@media (prefers-color-scheme: dark) {
  .system-performance-section {
    @apply bg-gray-800;
  }
  
  .section-title {
    @apply text-gray-100;
  }
  
  .filter-select {
    @apply bg-gray-700 border-gray-600 text-gray-100;
  }
  
  .performance-card {
    @apply bg-gray-700;
  }
  
  .card-title {
    @apply text-gray-300;
  }
  
  .main-value {
    @apply text-gray-100;
  }
  
  .sub-value {
    @apply text-gray-400;
  }
  
  .change-positive {
    @apply text-green-400;
  }
  
  .change-negative {
    @apply text-red-400;
  }
  
  .alerts-container {
    @apply bg-gray-700;
  }
  
  .alerts-title {
    @apply text-gray-100;
  }
  
  .alert-critical {
    @apply bg-red-900 bg-opacity-30 text-red-300;
  }
  
  .alert-warning {
    @apply bg-yellow-900 bg-opacity-30 text-yellow-300;
  }
  
  .alert-info {
    @apply bg-blue-900 bg-opacity-30 text-blue-300;
  }
  
  .action-button {
    @apply hover:bg-gray-600;
  }
}
</style> 